/**
 * marca.service.ts — lectura/edición de la marca singleton de la empresa.
 * El API responde y acepta camelCase, así que no hay remapeo de claves.
 */
import { httpGet, httpPut } from "@/shared/lib/http-client.js";
import {
  getCachedCatalogOnly,
  putCachedCatalog,
} from "@/features/offline-sync/services/catalog-cache.js";
import type { Marca } from "../types.js";

export async function getMarca(): Promise<Marca> {
  try {
    const marca = await httpGet<Marca>("/marca");
    // Fire-and-forget: cache an OFFLINE copy with logos inlined as data: URIs
    // so the brand theme + logos survive a later offline PDF render (S4-T4).
    // The live/online UI always gets the original https-logo marca below.
    void cacheOfflineMarca(marca);
    return marca;
  } catch (err) {
    // Network failed (offline, or backend unreachable) — fall back to the
    // last cached copy. If nothing was ever cached, surface the original error.
    const cached = await getCachedCatalogOnly<Marca>("marca");
    if (cached) return cached;
    throw err;
  }
}

/**
 * getMarcaForEdit — read the marca for the admin editor. Unlike getMarca, it
 * NEVER falls back to the offline cache. The cached copy stores logos as base64
 * data: URIs, and the edit form saves the whole object back via PUT; a fallback
 * here would let a transient GET failure load base64 logos into the form and
 * then overwrite the server's real Cloudinary URLs on save. On failure it throws
 * so the editor surfaces the error instead of silently corrupting the brand.
 */
export async function getMarcaForEdit(): Promise<Marca> {
  return httpGet<Marca>("/marca");
}

export async function updateMarca(payload: Partial<Marca>): Promise<Marca> {
  return httpPut<Marca>("/marca", payload);
}

// Signature of the last marca cached this session, to skip redundant re-inlining
// (BrandThemeLoader and every client PDF build call getMarca on each page/render).
let lastCachedMarcaSig: string | null = null;

/**
 * Build and persist an offline-ready copy of the marca: same fields, but
 * logoUrl/logoUrlClaro inlined as data: URIs so they can render without a
 * network round-trip. Never throws — a caching failure must not affect the
 * caller, which already has the (online) marca it needs.
 */
async function cacheOfflineMarca(marca: Marca): Promise<void> {
  const sig = JSON.stringify(marca);
  if (sig === lastCachedMarcaSig) return; // unchanged since last cache this session
  try {
    const [logoUrl, logoUrlClaro] = await Promise.all([
      inlineLogoToDataUri(marca.logoUrl),
      inlineLogoToDataUri(marca.logoUrlClaro),
    ]);
    await putCachedCatalog<Marca>("marca", { ...marca, logoUrl, logoUrlClaro });
    lastCachedMarcaSig = sig;
  } catch {
    // Best-effort only.
  }
}

/**
 * Fetch a logo URL and convert it to a data: URI. Resilient by design: a
 * missing, empty, or unreachable URL just falls back to the original string
 * (or undefined logos stay untouched) — this must never throw.
 */
async function inlineLogoToDataUri(url: string): Promise<string> {
  if (!url) return url;
  try {
    const res = await fetch(url);
    if (!res.ok) return url;
    const blob = await res.blob();
    return await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : url);
      reader.onerror = () => resolve(url);
      reader.readAsDataURL(blob);
    });
  } catch {
    return url;
  }
}
